import styled from "styled-components";
import { EditablePath } from "../editable/EditableContext";
import { WHITE, TINT_AMBER, TINT_RED, BORDER_TINT, TINT_GREEN, WASH, INK, BORDER_SUBTLE, FONT_FIGTREE, SEMAPHORE, TEXT_VALUE } from "../tokens";
import { Ed, at } from "./EditableLeaf";

// Hand-drawn SVG charts (MIKE-322 — vbars/waterfall/profile/trend/floodplan,
// plus roadzones/timeline from the Taracena N2 template). A direct port of the redesign's own SVG
// geometry (same field names, same math) rather than a charting library:
// there are only a handful of chart instances in the whole report, so a dependency
// would outweigh what it buys.

const ACC = SEMAPHORE.green.fill;
const ACCD = SEMAPHORE.green.text;
const AMB = SEMAPHORE.amber.text;
const MUT = INK.label;
const LINE = BORDER_SUBTLE;

type ChartTone = "accent" | "accentD" | "amber" | "muted";

const toneColor = (tone?: ChartTone): string =>
  tone === "amber" ? AMB : tone === "muted" ? INK.ghost : tone === "accentD" ? ACCD : ACC;

export const ChartBox = styled.div`
  border: 1px solid ${LINE};
  border-radius: 0.875rem;
  padding: 1rem 1.125rem 0.875rem;
  margin: 0.375rem 0 1rem;
  background: ${WHITE};
`;

export const ChartOverline = styled.div`
  font-size: 0.625rem;
  font-weight: 700;
  letter-spacing: 0.09em;
  text-transform: uppercase;
  color: ${MUT};
  margin-bottom: 0.875rem;
`;

export const ChartCaption = styled.div`
  margin-top: 0.75rem;
  font-size: 0.688rem;
  color: ${INK.quiet};
  line-height: 1.5;
  text-wrap: pretty;
`;

const FloodLegend = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.875rem;
  margin-top: 0.75rem;
`;

const FloodLegendItem = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  font-size: 0.656rem;
  font-weight: 600;
  color: ${INK.muted};
`;

const FloodLegendSwatch = styled.span<{ $color: string }>`
  width: 0.6875rem;
  height: 0.6875rem;
  border-radius: 0.1875rem;
  background: ${({ $color }) => $color};
  border: 1px solid rgba(0, 0, 0, 0.06);
`;

interface TextOpts {
  fs?: number;
  fw?: number;
  fill?: string;
  anchor?: "start" | "middle" | "end";
}

function chartText(x: number, y: number, s: string, opts: TextOpts = {}, key?: string) {
  return (
    <text
      key={key}
      x={x}
      y={y}
      fontFamily={FONT_FIGTREE}
      fill={opts.fill || TEXT_VALUE}
      fontSize={opts.fs || 11}
      fontWeight={opts.fw || 600}
      textAnchor={opts.anchor || "middle"}
      letterSpacing="-0.01em"
    >
      {s}
    </text>
  );
}

const SvgFrame = ({ viewBox, children }: { viewBox: string; children: React.ReactNode }) => (
  <svg viewBox={viewBox} style={{ width: "100%", height: "auto", display: "block", overflow: "visible" }}>
    {children}
  </svg>
);

// The geometry is driven by numbers the editor cannot type yet, so a chart is
// not something the admin can create — but its two texts are, and they were the
// only leaves in the report that no path ever reached.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function ReportChart({ chart, path }: { chart: any; path?: EditablePath }) {
  if (!chart || !chart.kind) return null;

  const body = (() => {
    switch (chart.kind) {
      case "vbars":
        return renderVBars(chart);
      case "waterfall":
        return renderWaterfall(chart);
      case "profile":
        return renderProfile(chart);
      case "trend":
        return renderTrend(chart);
      case "roadzones":
        return renderRoadzones(chart);
      case "timeline":
        return renderTimeline(chart);
      case "floodplan":
        return renderFloodplan();
      default:
        return null;
    }
  })();

  if (!body) return null;

  return (
    <ChartBox>
      {(chart.title || path) && <ChartOverline><Ed path={at(path, "title")} value={chart.title} placeholder="Título del gráfico" /></ChartOverline>}
      {body}
      {(chart.caption || path) && <ChartCaption><Ed path={at(path, "caption")} value={chart.caption} multiline placeholder="Pie del gráfico" /></ChartCaption>}
    </ChartBox>
  );
}

// Setback bands measured from the edge of a road (Ley 37/2015): the carriageway
// on the left, then one band per protection zone, with an optional dashed line
// marking where building becomes possible again.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function renderRoadzones(bl: any) {
  const W = 560, max = bl.max || 110, gy = 150, roadW = 66, x0 = roadW + 8;
  const xx = (m: number) => x0 + (m / max) * (W - x0 - 14);
  const fill: Record<string, string> = { red: TINT_RED, amber: TINT_AMBER, muted: WASH, green: TINT_GREEN[200] };
  const edge: Record<string, string> = { red: "#e8c3b7", amber: BORDER_TINT.amber, muted: BORDER_SUBTLE, green: BORDER_TINT.green };
  const ink: Record<string, string> = { red: SEMAPHORE.red.text, amber: AMB, muted: INK.muted, green: ACCD };
  const kids: React.ReactNode[] = [
    <rect key="road" x={4} y={62} width={roadW} height={gy - 62} rx={3} fill="#e7ecea" />,
  ];
  for (let i = 0; i < 3; i++) {
    kids.push(<line key={`rl${i}`} x1={10 + i * 22} y1={70} x2={10 + i * 22} y2={gy - 8} stroke={WHITE} strokeWidth={2} strokeDasharray="7 7" />);
  }
  kids.push(chartText(4 + roadW / 2, 54, bl.road || "", { fs: 9.5, fw: 800, fill: INK.soft }, "roadlbl"));
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (bl.bands || []).forEach((b: any, i: number) => {
    const a = xx(b.from), z = xx(b.to);
    const col = ink[b.tone] || ink.muted;
    kids.push(<rect key={`b${i}`} x={a} y={62} width={Math.max(z - a, 1)} height={gy - 62} fill={fill[b.tone] || fill.muted} stroke={edge[b.tone] || edge.muted} />);
    // A band too narrow for its own label gets it above the strip, on a leader line.
    const cxb = (a + z) / 2;
    const fits = z - a > String(b.label).length * 5.6 + 10;
    if (fits) {
      kids.push(chartText(cxb, 84 + i * 15, b.label, { fs: 10, fw: 700, fill: col }, `bl${i}`));
      kids.push(chartText(cxb, 97 + i * 15, `${b.from}–${b.to} m`, { fs: 9, fw: 600, fill: MUT }, `bm${i}`));
    } else {
      kids.push(chartText(cxb, 44, b.label, { fs: 10, fw: 700, fill: col }, `bl${i}`));
      kids.push(chartText(cxb, 55, `${b.from}–${b.to} m`, { fs: 9, fw: 600, fill: MUT }, `bm${i}`));
      kids.push(<line key={`ld${i}`} x1={cxb} y1={58} x2={cxb} y2={62} stroke={col} strokeWidth={1} opacity={0.5} />);
    }
  });
  if (bl.line) {
    const x = xx(bl.line.at);
    kids.push(<line key="lle" x1={x} y1={30} x2={x} y2={gy + 6} stroke={SEMAPHORE.red.text} strokeWidth={1.6} strokeDasharray="5 4" />);
    kids.push(chartText(x + 6, 26, bl.line.label, { fs: 10, fw: 800, fill: SEMAPHORE.red.fill, anchor: "start" }, "llelbl"));
    kids.push(<path key="ar" d={`M${x + 4} 40 L${W - 16} 40`} stroke={ACC} strokeWidth={1.4} />);
    kids.push(<path key="ah" d={`M${W - 22} 36 L${W - 16} 40 L${W - 22} 44`} fill="none" stroke={ACC} strokeWidth={1.4} />);
    kids.push(chartText((x + W - 16) / 2, 34, bl.line.right || "Zona edificable", { fs: 9.5, fw: 700, fill: ACCD }, "llerig"));
  }
  kids.push(<line key="g" x1={4} y1={gy} x2={W - 8} y2={gy} stroke="#c9d6cf" strokeWidth={1.2} />);
  [0, 8, 25, 50, 100].forEach((m, i) => {
    if (m > max) return;
    const x = xx(m);
    kids.push(<line key={`t${i}`} x1={x} y1={gy} x2={x} y2={gy + 5} stroke="#c9d6cf" />);
    kids.push(chartText(x, gy + 17, `${m} m`, { fs: 9, fw: 700, fill: MUT }, `tl${i}`));
  });
  return <SvgFrame viewBox={`0 0 ${W} ${gy + 26}`}>{kids}</SvgFrame>;
}

// Dated milestones on a single axis, alternating above and below so the labels
// never collide.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function renderTimeline(bl: any) {
  const its = bl.items || [], n = its.length || 1, W = 560, y = 112, x0 = 54, x1 = W - 54;
  const xx = (i: number) => (n === 1 ? (x0 + x1) / 2 : x0 + (x1 - x0) * (i / (n - 1)));
  const kids: React.ReactNode[] = [<line key="ax" x1={x0} y1={y} x2={x1} y2={y} stroke={LINE} strokeWidth={2} />];
  // Labels wrap at ~17 characters and never run past three lines.
  const wrapText = (t: string) => {
    const out: string[] = [];
    let cur = "";
    String(t).split(" ").forEach((p) => {
      if (`${cur} ${p}`.trim().length > 17) { out.push(cur.trim()); cur = p; }
      else cur += ` ${p}`;
    });
    if (cur.trim()) out.push(cur.trim());
    return out.slice(0, 3);
  };
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  its.forEach((it: any, i: number) => {
    const cx = xx(i), up = i % 2 === 0, col = it.tone ? toneColor(it.tone) : ACC;
    kids.push(<line key={`s${i}`} x1={cx} y1={y} x2={cx} y2={up ? y - 16 : y + 16} stroke={col} strokeWidth={1.2} opacity={0.5} />);
    kids.push(<circle key={`c${i}`} cx={cx} cy={y} r={it.mark ? 6 : 4.5} fill={it.mark ? col : WHITE} stroke={col} strokeWidth={2} />);
    // The first and last milestones anchor to the frame so they stay inside it.
    const anchor = i === 0 ? "start" : i === n - 1 ? "end" : "middle";
    const tx = i === 0 ? 4 : i === n - 1 ? W - 4 : cx;
    kids.push(chartText(tx, up ? y - 22 : y + 30, it.date, { fs: 10.5, fw: 800, fill: col, anchor }, `d${i}`));
    const lines = wrapText(it.label);
    lines.forEach((line, li) => kids.push(
      chartText(tx, up ? y - 34 - (lines.length - 1 - li) * 11 : y + 42 + li * 11, line, { fs: 9, fw: 600, fill: INK.muted, anchor }, `l${i}-${li}`)
    ));
  });
  return <SvgFrame viewBox={`0 0 ${W} ${y + 84}`}>{kids}</SvgFrame>;
}

function renderFloodplan() {
  const W = 560;
  const H = 292;
  return (
    <div>
      <SvgFrame viewBox={`0 0 ${W} ${H}`}>
        <rect x="2" y="2" width={W - 4} height={H - 4} rx="16" fill={WASH} stroke={LINE} />
        <path d="M560 34 L560 118 L214 288 L118 288 Z" fill="#d3e2ea" />
        <path d="M560 62 L560 92 L182 288 L150 288 Z" fill="#b7cfdb" />
        <path d="M560 118 L560 196 L346 288 L214 288 Z" fill="#e4edf2" opacity="0.75" />
        {chartText(430, 70, "Corredor Barranc de l’Horteta", { fs: 10, fw: 700, fill: "#5e7e8c" }, "cor")}
        {chartText(452, 210, "Huella DANA 29/10/2024", { fs: 10, fw: 700, fill: "#8aa0ac" }, "dana")}
        <rect x="116" y="176" width="68" height="44" rx="8" fill={ACC} />
        <circle cx="150" cy="198" r="5.5" fill={WHITE} />
        {chartText(150, 166, "Parcela", { fs: 10.5, fw: 800, fill: ACCD }, "parcel")}
        {chartText(150, 238, "42,31 m · margen sur", { fs: 9.5, fw: 700, fill: INK.muted }, "size")}
        {chartText(150, 251, "Fuera de la huella y de PATRICOVA", { fs: 9, fw: 600, fill: MUT }, "status")}
        <path d="M34 40 L34 20 M34 20 L29 27 M34 20 L39 27" stroke={INK.ghost} strokeWidth="1.4" fill="none" strokeLinecap="round" />
        {chartText(34, 52, "N", { fs: 9, fw: 800, fill: INK.ghost }, "north")}
      </SvgFrame>
      <FloodLegend>
        <FloodLegendItem><FloodLegendSwatch $color={ACC} />Parcela</FloodLegendItem>
        <FloodLegendItem><FloodLegendSwatch $color="#b7cfdb" />Corredor del barranco</FloodLegendItem>
        <FloodLegendItem><FloodLegendSwatch $color="#e4edf2" />Huella DANA</FloodLegendItem>
      </FloodLegend>
    </div>
  );
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function renderVBars(bl: any) {
  const bars = bl.bars || [];
  const n = bars.length || 1;
  const maxv = bl.ymax || Math.max(...bars.map((b: { v?: number }) => b.v || 0), 1);
  const W = 560, top = 24, baseY = 178, x0 = 14, area = W - 28, slot = area / n, barW = Math.min(60, slot * 0.5);
  const hasSub = bars.some((b: { sub?: string }) => b.sub);
  const kids: React.ReactNode[] = [
    <line key="base" x1={x0} y1={baseY} x2={W - 14} y2={baseY} stroke={LINE} strokeWidth={1} />,
  ];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  bars.forEach((b: any, i: number) => {
    const cx = x0 + slot * (i + 0.5);
    const h = ((b.v || 0) / maxv) * (baseY - top);
    const y = baseY - h;
    const col = toneColor(b.tone);
    if (b.mark) {
      kids.push(
        <rect key={`m${i}`} x={cx - barW / 2 - 3} y={y - 3} width={barW + 6} height={h + 6} rx={8} fill="none" stroke={col} strokeWidth={1.5} opacity={0.4} />
      );
    }
    kids.push(<rect key={`r${i}`} x={cx - barW / 2} y={y} width={barW} height={Math.max(h, 1)} rx={6} fill={col} opacity={b.tone === "muted" ? 0.85 : 1} />);
    kids.push(chartText(cx, y - 8, b.value, { fs: 12.5, fw: 800, fill: col }, `v${i}`));
    kids.push(chartText(cx, baseY + 17, b.label, { fs: 10.5, fw: 700, fill: INK.muted }, `l${i}`));
    if (b.sub) kids.push(chartText(cx, baseY + 31, b.sub, { fs: 9.5, fw: 600, fill: MUT }, `s${i}`));
  });
  return <SvgFrame viewBox={`0 0 560 ${baseY + (hasSub ? 40 : 24)}`}>{kids}</SvgFrame>;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function renderWaterfall(bl: any) {
  const steps = bl.steps || [];
  let run = 0;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const segs: Array<{ a: number; b: number; s: any }> = [];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  steps.forEach((s: any) => {
    let a: number, b: number;
    if (s.type === "base") { a = 0; b = s.v; run = s.v; }
    else if (s.type === "add") { a = run; b = run + s.v; run = b; }
    else if (s.type === "sub") { a = run; b = run - s.v; run = b; }
    else { a = 0; b = run; }
    segs.push({ a, b, s });
  });
  const maxv = Math.max(...segs.map((g) => Math.max(g.a, g.b)), 1);
  const W = 560, top = 26, baseY = 196, x0 = 14, n = steps.length || 1, slot = (W - 28) / n, barW = Math.min(70, slot * 0.5);
  const yy = (v: number) => baseY - (v / maxv) * (baseY - top);
  const kids: React.ReactNode[] = [
    <line key="b" x1={x0} y1={baseY} x2={W - 14} y2={baseY} stroke={LINE} strokeWidth={1} />,
  ];
  segs.forEach((g, i) => {
    const cx = x0 + slot * (i + 0.5);
    const yTop = yy(Math.max(g.a, g.b));
    const yBot = yy(Math.min(g.a, g.b));
    const col = g.s.type === "add" ? ACC : g.s.type === "sub" ? AMB : g.s.type === "total" ? ACCD : INK.quiet;
    kids.push(<rect key={`r${i}`} x={cx - barW / 2} y={yTop} width={barW} height={Math.max(yBot - yTop, 1)} rx={5} fill={col} opacity={g.s.type === "base" ? 0.85 : 1} />);
    kids.push(chartText(cx, yTop - 8, g.s.disp, { fs: 11.5, fw: 800, fill: col }, `v${i}`));
    kids.push(chartText(cx, baseY + 17, g.s.label, { fs: 10, fw: 700, fill: INK.muted }, `l${i}`));
    if (i < segs.length - 1) {
      const yr = yy(g.b);
      kids.push(
        <line key={`c${i}`} x1={cx + barW / 2} y1={yr} x2={x0 + slot * (i + 1.5) - barW / 2} y2={yr} stroke={MUT} strokeWidth={1} strokeDasharray="3 3" />
      );
    }
  });
  return <SvgFrame viewBox={`0 0 560 ${baseY + 26}`}>{kids}</SvgFrame>;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function renderProfile(bl: any) {
  const { altMin: aMin, altMax: aMax } = bl;
  const W = 560, top = 20, bot = 246, plotL = 44;
  const yy = (a: number) => bot - ((a - aMin) / (aMax - aMin)) * (bot - top);
  const kids: React.ReactNode[] = [];
  const gy = yy(bl.ground.alt);
  kids.push(<rect key="g" x={plotL} y={gy} width={W - plotL - 4} height={bot - gy} fill="#eef3f0" />);
  kids.push(<line key="gl" x1={plotL} y1={gy} x2={W - 4} y2={gy} stroke="#c9d6cf" strokeWidth={1} />);
  kids.push(chartText(W - 6, gy + 14, bl.ground.label, { fs: 9.5, fw: 700, fill: INK.quiet, anchor: "end" }, "gt"));

  const bx = 88, bw = 92;
  const yc = yy(bl.building.cornisa), yb = yy(bl.building.base), ya = yy(bl.building.antepecho);
  kids.push(<rect key="ap" x={bx} y={ya} width={bw} height={yc - ya} fill={ACC} opacity={0.22} />);
  kids.push(<rect key="bd" x={bx} y={yc} width={bw} height={yb - yc} rx={3} fill="#d5e5dd" stroke={ACC} strokeWidth={1.5} />);
  kids.push(chartText(bx + bw / 2, yc + (yb - yc) / 2 - 3, bl.building.label, { fs: 10, fw: 800, fill: ACCD }, "bl"));
  kids.push(chartText(bx + bw / 2, yc + (yb - yc) / 2 + 12, bl.building.sub, { fs: 8.5, fw: 700, fill: INK.muted }, "bs"));

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (bl.lines || []).forEach((ln: any, i: number) => {
    const y = yy(ln.alt);
    const col = toneColor(ln.tone);
    kids.push(<line key={`l${i}`} x1={plotL} y1={y} x2={W - 4} y2={y} stroke={col} strokeWidth={1.3} strokeDasharray="5 4" opacity={0.85} />);
    kids.push(chartText(W - 6, y - 6, ln.label, { fs: 9.5, fw: 700, fill: col, anchor: "end" }, `lt${i}`));
  });

  if (bl.band) {
    const bxc = 250, y1 = yy(bl.band.to), y2 = yy(bl.band.from);
    kids.push(<line key="ba" x1={bxc} y1={y1} x2={bxc} y2={y2} stroke={ACCD} strokeWidth={1.5} />);
    kids.push(<path key="a1" d={`M${bxc - 4} ${y1 + 6}L${bxc} ${y1}L${bxc + 4} ${y1 + 6}`} fill="none" stroke={ACCD} strokeWidth={1.5} />);
    kids.push(<path key="a2" d={`M${bxc - 4} ${y2 - 6}L${bxc} ${y2}L${bxc + 4} ${y2 - 6}`} fill="none" stroke={ACCD} strokeWidth={1.5} />);
    kids.push(<rect key="br" x={bxc + 8} y={(y1 + y2) / 2 - 12} width={104} height={24} rx={7} fill={TINT_GREEN[200]} stroke={BORDER_TINT.green} />);
    kids.push(chartText(bxc + 60, (y1 + y2) / 2 + 4, bl.band.label, { fs: 10.5, fw: 800, fill: ACCD }, "bt"));
  }

  return <SvgFrame viewBox="0 0 560 260">{kids}</SvgFrame>;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function renderTrend(bl: any) {
  const pts = bl.points || [];
  const W = 460, top = 22, bot = 118, x0 = 44, x1 = W - 28;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const vals = pts.map((p: any) => p.idx);
  const mn = Math.min(...vals), mx = Math.max(...vals), pad = (mx - mn) * 0.5 || 1;
  const yy = (v: number) => bot - ((v - (mn - pad)) / (mx + pad - (mn - pad))) * (bot - top);
  const xx = (i: number) => x0 + (x1 - x0) * (i / (pts.length - 1 || 1));
  const kids: React.ReactNode[] = [<line key="ax" x1={x0} y1={bot} x2={x1} y2={bot} stroke={LINE} />];
  let d = "";
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  pts.forEach((p: any, i: number) => { d += `${i ? "L" : "M"}${xx(i)} ${yy(p.idx)}`; });
  kids.push(<path key="ln" d={d} fill="none" stroke={ACC} strokeWidth={2} strokeLinejoin="round" strokeLinecap="round" />);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  pts.forEach((p: any, i: number) => {
    const col = p.tone ? toneColor(p.tone) : ACC;
    kids.push(<circle key={`d${i}`} cx={xx(i)} cy={yy(p.idx)} r={4.5} fill={WHITE} stroke={col} strokeWidth={2} />);
    kids.push(chartText(xx(i), bot + 16, p.label, { fs: 10, fw: 700, fill: INK.muted }, `pl${i}`));
    if (p.delta) kids.push(chartText(xx(i), yy(p.idx) - 12, p.delta, { fs: 11.5, fw: 800, fill: col }, `pd${i}`));
  });
  return <SvgFrame viewBox={`0 0 460 ${bot + 26}`}>{kids}</SvgFrame>;
}
